import React, { useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { Field } from '@components/framework/formfield/Field.tsx';
import { ValidationError } from 'yup';
import { hasValidationProblemForPrefix } from '@utils/validation.tsx';
import { InvoiceStateEnum } from '@api/ringil-api.v2.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import SvgIconFile from '@components/framework/icons/IconFile.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { toTestIdSlug } from '@utils/testUtils';

interface ComponentProps {
  secured?: SecuredConfig;
  value: InvoiceStateEnum;
  onChange: (value: InvoiceStateEnum) => void;
  readonly?: boolean;
  validationErrors?: ValidationError[];
  validationPrefix?: string;
}

const InternalInfoInvoiceState: React.FC<ComponentProps> = ({ secured, value, onChange, readonly, validationErrors, validationPrefix }) => {
  const [internalValue, setInternalValue] = useState<InvoiceStateEnum>(value);
  const { tr } = useTranslationLgs();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const options = [InvoiceStateEnum.NON_INVOICED, InvoiceStateEnum.INVOICED];

  const printValue = (value: InvoiceStateEnum) => {
    if (isNullOrUndefined(value)) return null;

    return tr(`InvoiceStateEnum.${value}`, value);
  };

  const hasError = validationErrors && validationPrefix ? hasValidationProblemForPrefix(validationErrors, validationPrefix) : false;

  const applyOverlayTestIds = () => {
    const panel = document.querySelector('.p-dropdown-panel.internalInfo-invoicingStateList') as HTMLElement | null;
    if (!panel) return;

    panel.setAttribute('data-testid', 'internalInfo-invoicingStateList-overlay');
  };

  useEffect(() => {
    // no-op: test ids are applied via React on wrapper and via onShow on overlay
  }, []);

  return (
    <>
      <ButtonTag secured={secured}
        label={tr("InternalInfoInvoiceStateTag.stavFakturace", "Stav fakturace")}
        icon={<SvgIconFile />}
        required={false}
        value={printValue(value)}
        readonly={readonly}
        onSave={(onSuccess) => {
          onChange(internalValue);
          onSuccess();
        }}
        autoClose
        dataTestId="internalInfo-invoicingState"
      >
        <>
          <Field label={tr("InternalInfoInvoiceStateTag.stavFakturace", "Stav fakturace")}>
            <div ref={wrapperRef} data-testid="internalInfo-invoicingStateList">
              <Dropdown
                value={internalValue}
                options={options}
                className="internalInfo-invoicingStateList-trigger w-full"
                showClear={true}
                panelClassName="internalInfo-invoicingStateList"
                valueTemplate={(e) => <div>{tr(`InvoiceStateEnum.${e}`, e)}</div>}
                itemTemplate={(e) => (
                  <div data-testid={`internalInfo-invoicingStateList-${toTestIdSlug(e)}`}>
                    {tr(`InvoiceStateEnum.${e}`, e)}
                  </div>
                )}
                onShow={() => {
                  requestAnimationFrame(applyOverlayTestIds);
                }}
                onChange={(e) => {
                  setInternalValue(e.value);
                }} />
            </div>
          </Field>
        </>
      </ButtonTag>
    </>);

};

export default InternalInfoInvoiceState;